import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, HelpCircle, Scale, ShoppingCart, Star, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtTL } from "../lib/api";
import { fmtQty4 } from "../lib/format";
import QualityBadge from "../components/QualityBadge";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

export default function ProductDetail() {
    const { productId } = useParams();
    const { user } = useAuth();
    const { refreshCart, toggleCompare, compareIds } = useShop();
    const [data, setData] = useState(null);
    const [qtys, setQtys] = useState({});
    const [askFor, setAskFor] = useState(null);
    const [qText, setQText] = useState("");
    const [qBusy, setQBusy] = useState(false);
    const [myQuestions, setMyQuestions] = useState([]);

    useEffect(() => {
        api.get(`/products/${productId}`).then((r) => setData(r.data)).catch(() => toast.error("Ürün yüklenemedi"));
    }, [productId]);

    const isBuyerRole = user?.role === "buyer";
    useEffect(() => {
        if (isBuyerRole) api.get("/questions").then((r) => setMyQuestions((r.data || []).filter((q) => q.product_id === productId))).catch(() => {});
    }, [productId, isBuyerRole]);

    const askQuestion = async () => {
        if (!qText.trim()) return toast.error("Sorunuzu yazın");
        setQBusy(true);
        try {
            await api.post("/questions", { supplier_id: askFor.supplier_id, product_id: productId, content: qText.trim() });
            toast.success(`Sorunuz ${askFor.supplier_name} tedarikçisine iletildi — yanıt süresi 1 takvim günü (Pazar hariç)`);
            setAskFor(null);
            setQText("");
            const r = await api.get("/questions");
            setMyQuestions((r.data || []).filter((q) => q.product_id === productId));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Soru gönderilemedi");
        } finally {
            setQBusy(false);
        }
    };

    const best = useMemo(() => data?.offers?.[0], [data]);

    const addToCart = async (offer) => {
        const qty = Number(qtys[offer.offer_id] ?? offer.moq);
        if (qty < offer.moq) return toast.error(`Minimum sipariş: ${offer.moq} ${data.unit}`);
        if (qty > offer.stock) return toast.error(`Yeterli stok yok (mevcut: ${offer.stock})`);
        try {
            await api.post("/cart/items", { offer_id: offer.offer_id, qty });
            await refreshCart();
            toast.success(`${data.name} sepete eklendi (${offer.supplier_name})`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Sepete eklenemedi");
        }
    };

    if (!data) return <div className="h-96 animate-pulse rounded-2xl bg-muted" />;

    const isBuyer = user?.role !== "supplier";

    return (
        <div className="space-y-8" data-testid="product-detail-page">
            <Link to="/panel/katalog" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground" data-testid="back-to-catalog">
                <ArrowLeft className="h-4 w-4" /> Kataloğa dön
            </Link>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <div className="overflow-hidden rounded-2xl border bg-card">
                        <img src={data.image} alt={data.name} className="aspect-square w-full object-cover" data-testid="product-image" />
                    </div>
                </div>
                <div className="lg:col-span-7">
                    <Badge variant="outline" className="text-xs">{data.category?.name}</Badge>
                    <h1 className="mt-2 font-display text-3xl font-bold tracking-tight" data-testid="product-name">{data.name}</h1>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{data.description}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {Object.entries(data.attributes || {}).map(([k, v]) => (
                            <span key={k} className="rounded-full bg-muted px-3 py-1 text-xs font-medium">{k}: <strong>{v}</strong></span>
                        ))}
                        <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium">SKU: <strong>{data.sku}</strong></span>
                        <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium">Birim: <strong>{data.unit}</strong></span>
                    </div>
                    {best && (
                        <div className="mt-6 flex items-center gap-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="best-offer-banner">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">En iyi fiyat</p>
                                <p className="font-display text-2xl font-extrabold text-emerald-800">{fmtTL(best.price)} <span className="text-sm font-medium">/ {data.unit} (KDV dahil)</span></p>
                            </div>
                            <div className="text-sm text-emerald-800">
                                <p className="font-semibold">{best.supplier_name}</p>
                                <p className="text-xs">{best.supplier_city} • {best.delivery_days} gün teslim</p>
                            </div>
                            {isBuyer && (
                                <Button className="ml-auto bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => addToCart(best)} data-testid="best-offer-add-cart">
                                    <ShoppingCart className="mr-2 h-4 w-4" /> Sepete Ekle
                                </Button>
                            )}
                        </div>
                    )}
                    {isBuyer && (
                        <Button
                            variant="outline"
                            className="mt-4"
                            onClick={() => { toggleCompare(productId); toast.success(compareIds.includes(productId) ? "Karşılaştırmadan çıkarıldı" : "Karşılaştırmaya eklendi"); }}
                            data-testid="product-compare-toggle"
                        >
                            <Scale className="mr-2 h-4 w-4" /> {compareIds.includes(productId) ? "Karşılaştırmadan Çıkar" : "Karşılaştırmaya Ekle"}
                        </Button>
                    )}
                </div>
            </div>

            <div data-testid="offers-comparison">
                <h2 className="font-display text-xl font-bold">Tedarikçi Karşılaştırması ({data.offers.length})</h2>
                <div className="mt-4 w-full overflow-x-auto rounded-xl border bg-card shadow-sm">
                    <table className="w-full text-sm" data-testid="offers-table">
                        <thead>
                            <tr className="border-b bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                                <th className="p-4">Tedarikçi</th>
                                <th className="p-4">Birim Fiyat (KDV dahil)</th>
                                <th className="p-4">Kademeli Fiyat</th>
                                <th className="p-4">MOQ</th>
                                <th className="p-4">Stok</th>
                                <th className="p-4">Teslim</th>
                                <th className="p-4">Güven</th>
                                {isBuyer && <th className="p-4 text-right">İşlem</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {data.offers.map((o, i) => (
                                <tr key={o.offer_id} className={`border-b last:border-0 transition-colors hover:bg-muted/40 ${i === 0 ? "bg-emerald-50/50" : ""}`} data-testid={`offer-row-${o.offer_id}`}>
                                    <td className="p-4">
                                        <Link to={`/panel/vitrin/${o.supplier_id}`} className="font-semibold hover:text-primary" data-testid={`offer-supplier-${o.offer_id}`}>
                                            {o.supplier_name}
                                        </Link>
                                        <p className="text-xs text-muted-foreground">{o.supplier_city} {o.verified && <BadgeCheck className="inline h-3.5 w-3.5 text-blue-600" />}</p>
                                        <QualityBadge quality={o.quality} tid={o.offer_id} />
                                    </td>
                                    <td className="p-4">
                                        {o.campaign ? (
                                            <>
                                                <span className="mr-2 text-xs text-muted-foreground line-through">{fmtTL(o.price)}</span>
                                                <span className="font-display text-base font-extrabold text-terra-600">{fmtTL(o.campaign_price)}</span>
                                                <Badge className="ml-2 border-0 bg-amber-500 text-[10px] text-white" data-testid={`offer-campaign-${o.offer_id}`}>Kampanya %{o.campaign.discount_percent}</Badge>
                                            </>
                                        ) : (
                                            <span className="font-display text-base font-extrabold text-terra-600">{fmtTL(o.price)}</span>
                                        )}
                                        {i === 0 && <Badge className="ml-2 bg-emerald-600 text-white border-0 text-[10px]">En Ucuz</Badge>}
                                    </td>
                                    <td className="p-4 text-xs text-muted-foreground">
                                        {(o.tiers || []).map((t) => <p key={t.min_qty}>{fmtQty4(t.min_qty, data.unit)}+ {data.unit}: <strong className="text-foreground">{fmtTL(t.price)}</strong></p>)}
                                    </td>
                                    <td className="p-4">{fmtQty4(o.moq, data.unit)} {data.unit}</td>
                                    <td className="p-4">
                                        <span className={o.stock < o.moq * 3 ? "font-semibold text-amber-700" : ""}>{fmtQty4(o.stock, data.unit)} {data.unit}</span>
                                    </td>
                                    <td className="p-4"><span className="inline-flex items-center gap-1"><Truck className="h-3.5 w-3.5 text-muted-foreground" /> {o.delivery_days} gün</span></td>
                                    <td className="p-4">
                                        <span className="inline-flex items-center gap-1 font-semibold text-sage-600"><Star className="h-3.5 w-3.5 fill-saffron-400 text-saffron-400" /> {o.trust_score}</span>
                                    </td>
                                    {isBuyer && (
                                        <td className="p-4">
                                            <div className="flex items-center justify-end gap-2">
                                                <Input
                                                    type="number"
                                                    min={o.moq}
                                                    value={qtys[o.offer_id] ?? o.moq}
                                                    onChange={(e) => setQtys({ ...qtys, [o.offer_id]: e.target.value })}
                                                    className="h-9 w-20"
                                                    data-testid={`qty-input-${o.offer_id}`}
                                                />
                                                <Button size="sm" onClick={() => addToCart(o)} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid={`add-cart-${o.offer_id}`}>
                                                    Sepete Ekle
                                                </Button>
                                                <Button size="sm" variant="ghost" title="Tedarikçiye soru sor" onClick={() => setAskFor({ supplier_id: o.supplier_id, supplier_name: o.supplier_name })} data-testid={`ask-question-${o.offer_id}`}>
                                                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                </Button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {isBuyerRole && myQuestions.length > 0 && (
                <div className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="product-qa-list">
                    <h2 className="font-display text-lg font-bold">Sorularım & Yanıtlar</h2>
                    <div className="mt-3 space-y-3">
                        {myQuestions.map((q) => (
                            <div key={q.question_id} className="rounded-xl border bg-muted/40 p-4" data-testid={`qa-item-${q.question_id}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm font-semibold">{q.supplier_name}</p>
                                    {q.answer ? (
                                        <Badge variant="outline" className="border-emerald-200 bg-emerald-100 text-[10px] text-emerald-800">Yanıtlandı{q.answered_late ? " (geç)" : ""}</Badge>
                                    ) : (
                                        <Badge variant="outline" className={`text-[10px] ${q.overdue ? "border-red-200 bg-red-100 text-red-700" : "border-amber-200 bg-amber-100 text-amber-800"}`}>
                                            {q.overdue ? "Yanıt süresi doldu" : `Yanıt bekleniyor (son: ${fmtDate(q.answer_due_at)})`}
                                        </Badge>
                                    )}
                                </div>
                                <p className="mt-1.5 text-sm">{q.content}</p>
                                {q.answer && <p className="mt-2 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-900"><strong>{q.answered_by || q.supplier_name}:</strong> {q.answer}</p>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <Dialog open={!!askFor} onOpenChange={() => setAskFor(null)}>
                <DialogContent data-testid="ask-question-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Tedarikçiye Soru Sor — {askFor?.supplier_name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-xs text-muted-foreground">{data?.name} hakkında sorunuz. Tedarikçi en geç 1 takvim günü içinde yanıtlamalıdır (Pazar tatil günüdür).</p>
                        <Textarea rows={3} value={qText} onChange={(e) => setQText(e.target.value)} placeholder="Sorunuz *" data-testid="ask-question-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAskFor(null)}>Vazgeç</Button>
                        <Button onClick={askQuestion} disabled={qBusy || !qText.trim()} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="ask-question-submit">
                            {qBusy ? "Gönderiliyor..." : "Soruyu Gönder"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
